import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { Icon } from '@ontruck/orc';

import { AssignmentDedicatedContracts } from 'modules/dedicated-contracts';
import { DedicatedContractsContextProvider } from 'modules/dedicated-contracts/assignment/context';
import { MyFleetSection } from 'modules/my-fleet';
import { MyFleetContextProvider } from 'modules/my-fleet/context';

import { AssignmentPreBooking } from 'modules/pre-booking';
import { PrebookingContextProvider } from 'modules/pre-booking/assignment/context';
import { launchToCustomPool } from 'modules/dispatching/service';

import { ReactComponent as ChevronLeftIcon } from 'assets/icons/chevron-left.svg';
import { ReactComponent as ContractsIcon } from 'assets/icons/contracts.svg';
import { ReactComponent as PrebookingsIcon } from 'assets/icons/prebookings.svg';
import { ReactComponent as CustomDriverIcon } from 'assets/icons/star.svg';
import { ReactComponent as TruckNearbyIcon } from 'assets/icons/truck-nearby-icon.svg';
import { ReactComponent as CustomPoolIcon } from 'assets/icons/not-offered.svg';
import { ReactComponent as AddIcon } from 'assets/icons/add.svg';

import AssignmentContext from './context';
import CustomAssignment from './custom';
import CustomPoolAssignment from './custom-pool';
import HeaderDate from './header-date';

import { DISPATCHING } from 'modules/fleet/events';
import { DriverPoolContextProvider } from '../../driver-pool/context';
import DriverPoolSection from '../../driver-pool/section';
import { injectIntl } from 'react-intl';
import { useDispatchingUI } from 'modules/dispatching/ui';
import { getClientConfig, constants } from 'redux/modules/client-config/selectors';
import { useSelector } from 'react-redux';

const AssignmentSection = ({ className, filters, beforeAssignment, afterAssignment, intl }) => {
  const SUBMENU = useSelector((state) => getClientConfig(state, constants.ASSIGNATION));
  const contracts = useMemo(
    () => (SUBMENU[constants.CONTRACTS_SECTION] ? { CONTRACTS: 'contracts' } : {}),
    [SUBMENU]
  );
  const prebookings = useMemo(
    () => (SUBMENU[constants.PREBOOKINGS_SECTION] ? { PREBOOKINGS: 'prebookings' } : {}),
    [SUBMENU]
  );
  const myFleet = useMemo(
    () => (SUBMENU[constants.MY_FLEET_SECTION] ? { MY_FLEET: 'my_fleet' } : {}),
    [SUBMENU]
  );
  const custom = useMemo(
    () => (SUBMENU[constants.CUSTOM_SECTION] ? { CUSTOM: 'custom' } : {}),
    [SUBMENU]
  );
  const customPool = useMemo(
    () => (SUBMENU[constants.CUSTOM_POOL_SECTION] ? { CUSTOM_POOL: 'custom_pool' } : {}),
    [SUBMENU]
  );
  const pool = useMemo(() => (SUBMENU[constants.POOL_SECTION] ? { POOL: 'pool' } : {}), [SUBMENU]);
  const TABS = useMemo(
    () => ({
      ...contracts,
      ...customPool,
      ...custom,
      ...myFleet,
      ...prebookings,
      ...pool,
    }),
    [contracts, customPool, pool, custom, myFleet, prebookings]
  );

  const tabIcons = {
    [TABS.POOL]: <TruckNearbyIcon />,
    [TABS.CONTRACTS]: <ContractsIcon />,
    [TABS.PREBOOKINGS]: <PrebookingsIcon />,
    [TABS.MY_FLEET]: <CustomDriverIcon />,
    [TABS.CUSTOM]: <AddIcon />,
    [TABS.CUSTOM_POOL]: <CustomPoolIcon />,
  };
  const labels = intl.messages.modules.dispatching.assignment;
  const t = {
    titles: {
      [TABS.POOL]: labels.titles.pool,
      [TABS.CONTRACTS]: labels.titles.contracts,
      [TABS.PREBOOKINGS]: labels.titles.prebookings,
      [TABS.MY_FLEET]: labels.titles.my_fleet,
      [TABS.CUSTOM]: labels.titles.custom,
      [TABS.CUSTOM_POOL]: labels.titles.custom_pool,
    },
    assign: labels.assign,
  };
  const { setLoading, mutateListFn } = useDispatchingUI();
  const { assignmentDate, itineraryToAssign, itineraryRoute, clearAssignment, assignItinerary } =
    useContext(AssignmentContext);

  const isOpened = !!assignmentDate;

  const [activeTab, setActiveTab] = useState(TABS.POOL);

  const handleVisibility = useCallback(() => {
    clearAssignment();
  }, [clearAssignment]);

  const handleActiveTab = useCallback(
    (e) => {
      const active = e.target.getAttribute('section');
      setActiveTab(active);
    },
    [setActiveTab]
  );

  const routesFilter = itineraryRoute ? [{ value: itineraryRoute.origin }] : filters?.route;

  const contractFilters = {
    date: assignmentDate,
    routes: routesFilter,
  };

  const prebookingFilters = {
    routes: routesFilter,
    date: assignmentDate,
    vehicle_type: filters?.vehicle_type,
  };

  const myFleetFilters = {
    date: assignmentDate,
    routes: routesFilter,
  };

  const driversPoolFilters = {
    itineraryId: itineraryToAssign,
    date: assignmentDate,
  };

  const handleCustomAssignment = useCallback(
    async ({ driverId, assignmentReason }) => {
      clearAssignment();
      beforeAssignment({ itineraryToAssign });
      await assignItinerary({ driverId, itineraryToAssign, assignmentReason });
      DISPATCHING.MAKE_ASSIGNMENT({
        driver_id: driverId,
        itinerary_id: itineraryToAssign,
        active_tab: activeTab,
      });
      afterAssignment({ itineraryToAssign });
    },
    [
      itineraryToAssign,
      clearAssignment,
      assignItinerary,
      activeTab,
      beforeAssignment,
      afterAssignment,
    ]
  );

  const handleCustomPoolAssignment = useCallback(
    async ({ drivers }) => {
      clearAssignment();
      const itineraryId = itineraryToAssign;
      try {
        setLoading((loading) => ({ ...loading, [itineraryId]: true }));
        await launchToCustomPool({ itineraryId, drivers });
        await mutateListFn();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((loading) => ({ ...loading, [itineraryId]: false }));
      }
    },
    [itineraryToAssign, setLoading, mutateListFn, clearAssignment]
  );

  const AssignActionComponent = ({ driverId, vehicleIds, prebookingId }) => {
    const handleAssignment = useCallback(async () => {
      clearAssignment();
      beforeAssignment({ itineraryToAssign });
      await assignItinerary({ driverId, vehicleIds, itineraryToAssign, prebookingId });
      DISPATCHING.MAKE_ASSIGNMENT({
        driver_id: driverId,
        vehicle_ids: vehicleIds,
        itinerary_id: itineraryToAssign,
        active_tab: activeTab,
      });
      afterAssignment({ itineraryToAssign });
    }, [driverId, vehicleIds, prebookingId]);

    return (
      <button className="assign-button" onClick={handleAssignment}>
        {t.assign}
      </button>
    );
  };

  const removeOutCustomAssignment = (tab) => {
    if (![TABS.CUSTOM].includes(tab)) {
      return true;
    }

    if (itineraryToAssign) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (isOpened) {
      return;
    }
    setActiveTab(TABS.POOL);
  }, [isOpened, TABS.POOL]);

  useEffect(() => {
    if (activeTab === TABS.CUSTOM && !itineraryToAssign) {
      const activeTab = Object.keys(TABS).filter((key) => TABS[key]);
      setActiveTab(TABS.CONTRACTS ? TABS.CONTRACTS : TABS[activeTab[0]]);
      return;
    }
  }, [activeTab, itineraryToAssign, TABS]);

  if (!isOpened) {
    return null;
  }

  return (
    <div className={className} data-testid="DispatchingAssignmentSection">
      <div className="toolbar">
        <button className="toolbar__item" onClick={handleVisibility}>
          {isOpened ? <Icon glyph="close" size="1.6rem" color="white" /> : <ChevronLeftIcon />}
        </button>
        {Object.values(TABS)
          .filter(removeOutCustomAssignment)
          .map((tab) => (
            <button
              key={tab}
              className={cx('toolbar__item', {
                selected: activeTab === tab,
              })}
              data-testid={`DispatchingAssignmentSection__button__${tab}`}
              onClick={handleActiveTab}
              section={tab}
            >
              {tabIcons[tab]}
            </button>
          ))}
      </div>
      {isOpened && (
        <div className="content">
          {activeTab === TABS.POOL && (
            <div>
              <HeaderDate date={assignmentDate} />
              <div className="title">{t.titles[TABS.POOL]}</div>
              <DriverPoolContextProvider filters={driversPoolFilters}>
                <DriverPoolSection
                  vertical
                  assignActionComponent={itineraryToAssign ? AssignActionComponent : null}
                />
              </DriverPoolContextProvider>
            </div>
          )}
          {activeTab === TABS.CONTRACTS && (
            <div>
              <HeaderDate date={assignmentDate} />
              <div className="title">{t.titles[TABS.CONTRACTS]}</div>
              <DedicatedContractsContextProvider>
                <AssignmentDedicatedContracts
                  vertical
                  filters={contractFilters}
                  assignActionComponent={itineraryToAssign ? AssignActionComponent : null}
                />
              </DedicatedContractsContextProvider>
            </div>
          )}
          {activeTab === TABS.PREBOOKINGS && (
            <div>
              <HeaderDate date={assignmentDate} />
              <div className="title">{t.titles[TABS.PREBOOKINGS]}</div>
              <PrebookingContextProvider>
                <AssignmentPreBooking
                  vertical
                  filters={prebookingFilters}
                  assignActionComponent={itineraryToAssign ? AssignActionComponent : null}
                />
              </PrebookingContextProvider>
            </div>
          )}
          {activeTab === TABS.MY_FLEET && (
            <div>
              <div className="title">{t.titles[TABS.MY_FLEET]}</div>
              <MyFleetContextProvider filters={myFleetFilters}>
                <MyFleetSection
                  vertical
                  assignActionComponent={itineraryToAssign ? AssignActionComponent : null}
                />
              </MyFleetContextProvider>
            </div>
          )}
          {activeTab === TABS.CUSTOM_POOL && (
            <div>
              <div className="title">{t.titles[TABS.CUSTOM_POOL]}</div>
              <CustomPoolAssignment route={itineraryRoute} onAssign={handleCustomPoolAssignment} />
            </div>
          )}
          {activeTab === TABS.CUSTOM && (
            <div>
              <div className="title">{t.titles[TABS.CUSTOM]}</div>
              <CustomAssignment route={itineraryRoute} onAssign={handleCustomAssignment} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default injectIntl(AssignmentSection);
